import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import i18n from '../i18n';
import { jobKeys } from './useJobOffers';
import { usageKeys } from './useUsage';

class FatalError extends Error { }
class RetriableError extends Error { }

// Type definitions for SSE events
export interface JobNotificationEvent {
  event: string;
  message: string;
  job_id?: string;
  timestamp?: string;
  details?: Record<string, unknown>;
}

export interface UseGlobalNotificationsOptions {
  enabled?: boolean;
  onNotification?: (event: JobNotificationEvent) => void;
}


/**
 * Hook for global user notifications 
 * 
 * Uses a single SSE connection to receive notifications for all user's jobs.
 * Much more efficient than per-job connections - avoids browser connection limits.
 * 
 * Features:
 * - Single SSE connection for all jobs
 * - Global toast notifications for all events
 * - Job ID included in event data to identify which job the notification relates to
 * - Automatic reconnection on failures
 * - Non-blocking connection establishment
 */
export const useGlobalJobNotifications = ({
  enabled = true,
  onNotification
}: UseGlobalNotificationsOptions) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Clean up connection
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect to global user notifications
  const connect = useCallback(async () => {
    if (!enabled || !isAuthenticated) {
      return;
    }

    try {
      // Clean up existing connection
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Get access token for authentication
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        },
      });
      
      // Create the SSE URL for global notifications
      const baseUrl = import.meta.env.VITE_API_SERVER_URL || '';
      const url = `${baseUrl}/api/jobs/notifications/events`;
      
      // Create AbortController for cancellation
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Use Microsoft's fetch-event-source with callbacks
      await fetchEventSource(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
        
        // Configure visibility behavior
        openWhenHidden: true, // Keep connection open when page is hidden
        
        async onopen(response) {
          if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
            console.log(`[GlobalSSE] Connected to global notification stream`);
            setIsConnected(true);
            // Fires on reconnect too (Cloud Run forces periodic SSE drops) —
            // refetch as a safety net for any event missed during the gap.
            queryClient.invalidateQueries({ queryKey: jobKeys.all });
            queryClient.invalidateQueries({ queryKey: usageKeys.myPlan() });
          } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            console.error(`[GlobalSSE] Client error (${response.status}) - stopping retries`);
            throw new FatalError(`Client error: ${response.status} ${response.statusText}`);
          } else {
            throw new RetriableError(`Server error: ${response.status} ${response.statusText}`);
          }
        },
        
        onmessage(event) {
          try {
            const data: JobNotificationEvent = JSON.parse(event.data);
            console.log(`[GlobalSSE] Received notification:`, data);
            
            // Call custom handler if provided
            if (onNotification) {
              onNotification(data);
            }

            // Handle specific event types with toast notifications
            switch (data.event) {
              case 'connected':
                // Don't show toast for connection events
                break;
              
              case 'job_analysis_error':
                toast.error(i18n.t('common:notifications.job_analysis_failed'), {
                  duration: 8000,
                  description: data.message,
                });
                break;
              
              case 'cv_processing_failed':
                // Only show individual failure notifications if not in batch processing mode
                // Batch processing notifications are handled by the JobDetailsPage component
                console.log('[GlobalSSE] CV processing failed (suppressed individual notification):', data.message);
                break;
              
              case 'job_analysis_complete':
                toast.success(i18n.t('common:notifications.job_analysis_complete'), {
                  duration: 4000,
                  description: data.message,
                });
                break;
              
              case 'cv_processing_success':
                // Don't show toast here - JobDetailsPage handles this for better context
                // Global handler in App.tsx will still invalidate queries
                console.log('[GlobalSSE] CV processing success event (toast handled by JobDetailsPage)');
                break;
              
              case 'score_recomputed_success':
                // Don't show toast here - App.tsx handles this with custom handler
                console.log('[GlobalSSE] Score recomputed success event (toast handled by App.tsx)');
                break;
              
              case 'duplicate_candidate_detected':
                toast.warning(i18n.t('common:notifications.duplicate_candidate'), {
                  duration: 8000,
                  description: data.message,
                });
                break;
              
              case 'usage_limit_exceeded':
                // Don't show toast here - App.tsx handles this with upgrade modal
                console.log('[GlobalSSE] Usage limit exceeded (modal handled by App.tsx)');
                break;
              
              case 'plan_expired':
                toast.error(i18n.t('common:notifications.plan_expired'), {
                  duration: 10000,
                  description: data.message,
                });
                break;
              
              case 'error':
              case 'connection_error':
                toast.error(i18n.t('common:notifications.connection_error'), {
                  duration: 5000,
                  description: data.message,
                });
                break;
              
              case 'resume_bank_batch_complete':
                // Handled by App.tsx - don't show default toast
                console.log('[GlobalSSE] Resume bank batch complete (toast handled by App.tsx)');
                break;

              case 'social_profile_scoring_started':
              case 'social_profile_scoring_completed':
              case 'social_profile_scoring_failed':
                // Candidate-level social scoring flow is handled by CandidateInfo.
                console.log(`[GlobalSSE] ${data.event} (handled by CandidateInfo/App handlers)`);
                break;
              
              default:
                // Show info toast for unknown events
                toast.info(data.message, {
                  duration: 4000
                });
                break;
            }
          } catch (error) {
            console.error(`[GlobalSSE] Error parsing notification data:`, error);
          }
        },
        
        onerror(error) {
          console.log(`[GlobalSSE] Connection error:`, error);
          setIsConnected(false);
          
          if (error instanceof FatalError) {
            throw error;
          } else {
            console.error(`[GlobalSSE] Unexpected error, will be retried:`, error);
          }
        },
        
        onclose() {
          console.log(`[GlobalSSE] Connection closed`);
          setIsConnected(false);
          abortControllerRef.current = null;
        }
      });

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log(`[GlobalSSE] Connection aborted`);
      } else {
        console.error(`[GlobalSSE] Failed to connect to global notifications:`, error);
      }
      setIsConnected(false);
      abortControllerRef.current = null;
    }
  }, [enabled, isAuthenticated, getAccessTokenSilently, onNotification, queryClient]);

  // Effect to auto-connect when enabled and authenticated
  // Add a delay to let the app fully load first
  useEffect(() => {
    if (enabled && isAuthenticated) {
      // Delay connection to not block initial app load
      const timeoutId = setTimeout(() => {
        // Fire-and-forget connection - don't await to avoid blocking
        connect().catch(error => {
          console.warn('[GlobalSSE] Background connection failed:', error);
        });
      }, 1500); // 1.5 second delay
      
      return () => {
        clearTimeout(timeoutId);
        cleanup();
      };
    } else {
      cleanup();
    }
  }, [enabled, isAuthenticated, connect, cleanup]);

  // Effect to clean up on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Return connection status and manual controls
  return {
    isConnected,
    connect,
    disconnect: cleanup
  };
};
